import { api } from '../../../lib/api';

// WhatsApp types

/** One of the store's WhatsApp business numbers. */
export interface WhatsAppNumber {
  /** Null for a store not yet on number rows (its one legacy number). */
  id: string | null;
  phoneNumberId: string;
  wabaId?: string;
  displayNumber?: string;
  label?: string;
  status: 'active' | 'disconnected';
  isDefault: boolean;
  configured: boolean;
  setupMethod?: string;
}

export interface WhatsAppStatus {
  configured: boolean;
  provider?: 'cloud_api' | 'baileys' | null;
  // The default number, for screens that know only one.
  whatsappNumber?: string;
  phoneNumberId?: string;
  businessAccountId?: string;
  numbers: WhatsAppNumber[];
}

export interface WhatsAppConfigData {
  phone_number_id: string;
  business_account_id: string;
  access_token: string;
  app_secret: string;
  webhook_verify_token: string;
  whatsapp_number: string;
  label?: string;
}

export interface WhatsAppConfigResponse {
  phoneNumberId: string;
  businessAccountId: string;
  whatsappNumber: string;
  webhookUrl: string;
  configured: boolean;
  isDefault?: boolean;
}

export interface EmbeddedSignupCallbackData {
  code: string;
  waba_id: string;
  phone_number_id: string;
  label?: string;
}

export interface BaileysConnectResponse {
  sessionId: string;
  qrCode: string;
}

export interface BaileysStatus {
  connected: boolean;
  phoneNumber?: string;
  sessionId?: string;
  qrCode?: string;
}

// Shopify types
export interface ShopifyStatus {
  connected: boolean;
  shopUrl?: string;
  siteUrl?: string;
  platformType: string;
  hasApiKey: boolean;
  hasApiSecret: boolean;
  hasAccessToken: boolean;
}

export interface ShopifyConnectData {
  shop_url: string;
  site_url?: string;
  api_key: string;
  api_secret: string;
  access_token: string;
}

export interface ShopifySyncResult {
  status: string;
  policies_synced: number;
  pages_synced: number;
  shipping_zones_synced: number;
  synced_at: string;
  warnings?: string[];
}

// Store types
export interface StoreLocation {
  name: string;
  address: string;
  city: string;
  country: string;
  hours: string;
}

export interface StorePhone {
  label: string;
  number: string;
}

export interface StoreInfo {
  id: string;
  name: string;
  platformType: string;
  active: boolean;
  whatsappNumber?: string;
  businessBriefing: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  websiteUrl: string | null;
  businessHours: string | null;
  locations: StoreLocation[];
  phones: StorePhone[];
  createdAt: string;
}

// Wire shape sent to PATCH /stores/me. Each list field is optional —
// the backend strips empty strings and drops blank rows.
export interface StoreUpdateData {
  name?: string;
  business_briefing?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  website_url?: string | null;
  business_hours?: string | null;
  locations?: Array<{
    name?: string | null;
    address?: string | null;
    city?: string | null;
    country?: string | null;
    hours?: string | null;
  }> | null;
  phones?: Array<{
    label?: string | null;
    number?: string | null;
  }> | null;
}

// Mirror of backend BUSINESS_BRIEFING_MAX_LENGTH in app/api/schemas.py
export const BUSINESS_BRIEFING_MAX_LENGTH = 8000;
export const BUSINESS_HOURS_MAX_LENGTH = 500;

// Mirrors backend MAX_STORE_LOCATIONS in app/api/schemas.py.
export const MAX_STORE_LOCATIONS = 50;
export const LOCATION_NAME_MAX_LENGTH = 120;
export const LOCATION_ADDRESS_MAX_LENGTH = 500;
export const LOCATION_CITY_MAX_LENGTH = 120;
export const LOCATION_COUNTRY_MAX_LENGTH = 120;
export const LOCATION_HOURS_MAX_LENGTH = 500;

// Mirrors backend MAX_STORE_PHONES in app/api/schemas.py.
export const MAX_STORE_PHONES = 20;
export const PHONE_LABEL_MAX_LENGTH = 80;
export const PHONE_NUMBER_MAX_LENGTH = 50;

// CSAT settings — mirrors backend StoreCsatSettings in app/api/schemas.py
export interface CsatSettings {
  enabled: boolean;
  promptDelayMinutes: number;
  promptText: string;
  thankyouText: string | null;
}

export interface CsatSettingsUpdate {
  enabled?: boolean;
  prompt_delay_minutes?: number;
  prompt_text?: string;
  thankyou_text?: string | null;
}

// Clerk facts export — mirrors backend StoreClerkFactsSettings in app/api/schemas.py
export interface ClerkFactsSettings {
  enabled: boolean;
  clerkConnected: boolean;
}

export const CSAT_PROMPT_DELAY_MIN = 1;
export const CSAT_PROMPT_DELAY_MAX = 10;
export const CSAT_PROMPT_TEXT_MAX_LENGTH = 280;

// AI Responsibilities — scheduled, plain-English duties the AI runs and posts
// to the team chat. Mirrors backend AIResponsibility in app/api/schemas.py.
export interface AiResponsibilitySchedule {
  time: string; // "HH:MM" 24h, store-local
  days: number[]; // 0=Mon .. 6=Sun
}

export interface AiResponsibility {
  id: string;
  instruction: string;
  schedule: AiResponsibilitySchedule;
  enabled: boolean;
  createdAt: string | null;
  updatedAt: string | null;
  createdBy: string | null;
  lastRunAt: string | null;
  lastRunStatus: string | null;
}

export interface AiResponsibilityCreate {
  instruction: string;
  schedule: AiResponsibilitySchedule;
  enabled: boolean;
}

export interface AiResponsibilityUpdate {
  instruction?: string;
  schedule?: AiResponsibilitySchedule;
  enabled?: boolean;
}

export const AI_RESPONSIBILITY_INSTRUCTION_MAX_LENGTH = 2000;
export const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function mapResponsibility(data: any): AiResponsibility {
  return {
    id: data.id,
    instruction: data.instruction ?? '',
    schedule: {
      time: data.schedule?.time ?? '09:00',
      days: Array.isArray(data.schedule?.days) ? data.schedule.days : [],
    },
    enabled: !!data.enabled,
    createdAt: data.created_at ?? null,
    updatedAt: data.updated_at ?? null,
    createdBy: data.created_by ?? null,
    lastRunAt: data.last_run_at ?? null,
    lastRunStatus: data.last_run_status ?? null,
  };
}

export function mapWhatsAppNumber(data: any): WhatsAppNumber {
  return {
    id: data.id ?? null,
    phoneNumberId: data.phone_number_id,
    wabaId: data.waba_id ?? undefined,
    displayNumber: data.display_number ?? undefined,
    label: data.label ?? undefined,
    status: data.status === 'disconnected' ? 'disconnected' : 'active',
    isDefault: !!data.is_default,
    configured: !!data.configured,
    setupMethod: data.setup_method ?? undefined,
  };
}

function mapWhatsAppStatus(data: any): WhatsAppStatus {
  return {
    configured: data.configured,
    provider: data.provider || null,
    whatsappNumber: data.whatsapp_number,
    phoneNumberId: data.phone_number_id,
    businessAccountId: data.business_account_id,
    numbers: (data.numbers ?? []).map(mapWhatsAppNumber),
  };
}

function mapWhatsAppConfig(data: any): WhatsAppConfigResponse {
  return {
    phoneNumberId: data.phone_number_id,
    businessAccountId: data.business_account_id,
    whatsappNumber: data.whatsapp_number,
    webhookUrl: data.webhook_url,
    configured: data.configured,
    isDefault: data.is_default ?? undefined,
  };
}

function mapShopifyStatus(data: any): ShopifyStatus {
  return {
    connected: data.connected,
    shopUrl: data.shop_url,
    siteUrl: data.site_url,
    platformType: data.platform_type,
    hasApiKey: data.has_api_key,
    hasApiSecret: data.has_api_secret,
    hasAccessToken: data.has_access_token,
  };
}

function mapCsatSettings(data: any): CsatSettings {
  return {
    enabled: !!data.enabled,
    promptDelayMinutes: data.prompt_delay_minutes ?? 2,
    promptText: data.prompt_text ?? '',
    thankyouText: data.thankyou_text ?? null,
  };
}

function mapClerkFactsSettings(data: any): ClerkFactsSettings {
  return {
    enabled: !!data.enabled,
    clerkConnected: !!data.clerk_connected,
  };
}

function mapStoreInfo(data: any): StoreInfo {
  return {
    id: data.id,
    name: data.name,
    platformType: data.platform_type,
    active: data.active,
    whatsappNumber: data.whatsapp_number,
    businessBriefing: data.business_briefing ?? null,
    contactEmail: data.contact_email ?? null,
    contactPhone: data.contact_phone ?? null,
    websiteUrl: data.website_url ?? null,
    businessHours: data.business_hours ?? null,
    locations: Array.isArray(data.locations)
      ? data.locations.map((loc: any) => ({
          name: loc?.name ?? '',
          address: loc?.address ?? '',
          city: loc?.city ?? '',
          country: loc?.country ?? '',
          hours: loc?.hours ?? '',
        }))
      : [],
    phones: Array.isArray(data.phones)
      ? data.phones.map((ph: any) => ({
          label: ph?.label ?? '',
          number: ph?.number ?? '',
        }))
      : [],
    createdAt: data.created_at,
  };
}

export const settingsService = {
  // WhatsApp
  async getWhatsAppStatus(): Promise<WhatsAppStatus> {
    const data = await api.get<any>('/whatsapp/status');
    return mapWhatsAppStatus(data);
  },

  async configureWhatsApp(config: WhatsAppConfigData): Promise<WhatsAppConfigResponse> {
    const data = await api.post<any>('/whatsapp/configure', config);
    return mapWhatsAppConfig(data);
  },

  async embeddedSignupCallback(payload: EmbeddedSignupCallbackData): Promise<WhatsAppConfigResponse> {
    const data = await api.post<any>('/whatsapp/embedded-signup/callback', payload);
    return mapWhatsAppConfig(data);
  },

  async getWhatsAppNumbers(): Promise<WhatsAppNumber[]> {
    const data = await api.get<any[]>('/whatsapp/numbers');
    return data.map(mapWhatsAppNumber);
  },

  async renameWhatsAppNumber(id: string, label: string): Promise<WhatsAppNumber> {
    const data = await api.patch<any>(`/whatsapp/numbers/${id}`, { label });
    return mapWhatsAppNumber(data);
  },

  async setDefaultWhatsAppNumber(id: string): Promise<WhatsAppNumber> {
    const data = await api.post<any>(`/whatsapp/numbers/${id}/default`);
    return mapWhatsAppNumber(data);
  },

  async disconnectWhatsAppNumber(id: string, newDefaultId?: string): Promise<WhatsAppNumber> {
    const data = await api.delete<any>(`/whatsapp/numbers/${id}`, {
      params: newDefaultId ? { new_default_id: newDefaultId } : undefined,
    });
    return mapWhatsAppNumber(data);
  },

  // Baileys
  async connectBaileys(): Promise<BaileysConnectResponse> {
    const data = await api.post<any>('/whatsapp/baileys/connect');
    return { sessionId: data.session_id, qrCode: data.qr_code };
  },

  async getBaileysQR(): Promise<{ qrCode: string }> {
    const data = await api.get<any>('/whatsapp/baileys/qr');
    return { qrCode: data.qr_code };
  },

  async getBaileysStatus(): Promise<BaileysStatus> {
    const data = await api.get<any>('/whatsapp/baileys/status');
    return { connected: data.connected, phoneNumber: data.phone_number, sessionId: data.session_id, qrCode: data.qr_code };
  },

  async disconnectBaileys(): Promise<void> {
    await api.post<any>('/whatsapp/baileys/disconnect');
  },

  // Shopify
  async getShopifyStatus(): Promise<ShopifyStatus> {
    const data = await api.get<any>('/stores/shopify/status');
    return mapShopifyStatus(data);
  },

  async connectShopify(config: ShopifyConnectData): Promise<StoreInfo> {
    const data = await api.post<any>('/stores/shopify/connect', config);
    return mapStoreInfo(data);
  },

  async disconnectShopify(): Promise<StoreInfo> {
    const data = await api.delete<any>('/stores/shopify/disconnect');
    return mapStoreInfo(data);
  },

  async syncShopifyContent(): Promise<ShopifySyncResult> {
    return api.post<ShopifySyncResult>('/stores/shopify/sync');
  },

  // Store
  async getStore(): Promise<StoreInfo> {
    const data = await api.get<any>('/stores/me');
    return mapStoreInfo(data);
  },

  async updateStore(update: StoreUpdateData): Promise<StoreInfo> {
    const data = await api.patch<any>('/stores/me', update);
    return mapStoreInfo(data);
  },

  // CSAT (customer feedback)
  async getCsatSettings(): Promise<CsatSettings> {
    const data = await api.get<any>('/stores/csat-settings');
    return mapCsatSettings(data);
  },

  async updateCsatSettings(update: CsatSettingsUpdate): Promise<CsatSettings> {
    const data = await api.patch<any>('/stores/csat-settings', update);
    return mapCsatSettings(data);
  },

  // Clerk facts export
  async getClerkFactsSettings(): Promise<ClerkFactsSettings> {
    const data = await api.get<any>('/stores/clerk-facts-settings');
    return mapClerkFactsSettings(data);
  },

  async updateClerkFactsSettings(enabled: boolean): Promise<ClerkFactsSettings> {
    const data = await api.patch<any>('/stores/clerk-facts-settings', { enabled });
    return mapClerkFactsSettings(data);
  },

  // AI Responsibilities
  async getResponsibilities(): Promise<AiResponsibility[]> {
    const data = await api.get<any[]>('/stores/ai-responsibilities');
    return data.map(mapResponsibility);
  },

  async createResponsibility(payload: AiResponsibilityCreate): Promise<AiResponsibility> {
    const data = await api.post<any>('/stores/ai-responsibilities', payload);
    return mapResponsibility(data);
  },

  async updateResponsibility(id: string, update: AiResponsibilityUpdate): Promise<AiResponsibility> {
    const data = await api.patch<any>(`/stores/ai-responsibilities/${id}`, update);
    return mapResponsibility(data);
  },

  async deleteResponsibility(id: string): Promise<void> {
    await api.delete<void>(`/stores/ai-responsibilities/${id}`);
  },

  async runResponsibilityNow(id: string): Promise<AiResponsibility> {
    const data = await api.post<any>(`/stores/ai-responsibilities/${id}/run-now`);
    return mapResponsibility(data);
  },
};
